import React, { useEffect } from "react";
import { Plus, Trash } from "lucide-react";

import { withoutReservedTags } from "@formant/core/common/withoutReservedTags";
import { ICommandParameterModel } from "@formant/core/model/commandTemplate";
import { ITags } from "@formant/core/model/ITags";
import {
    Button,
    FormControl,
    FormItem,
    FormLabel,
    Input,
    ITypeaheadOption,
    SearchableDropdown,
    TypeaheadInput,
} from "@formant/ui-kit";

import { deviceStore } from "../../../../../stores";
import { convertTagsToTypeaheadOptions } from "../../utils/convertTagsToTypeaheadOptions";

interface IOptionsParameterProps {
    param: ICommandParameterModel;
    onUpdate: (updates: Partial<ICommandParameterModel>) => void;
}

export const OptionsParameter: React.FC<IOptionsParameterProps> = ({
    param,
    onUpdate,
}) => {
    const [existingTagsOptions, setExistingTagsOptions] = React.useState<
        ITypeaheadOption[]
    >([]);
    const [newLabel, setNewLabel] = React.useState("");
    const [newValue, setNewValue] = React.useState("");
    const [newDescription, setNewDescription] = React.useState("");
    const [newTags, setNewTags] = React.useState<ITags>({});

    useEffect(() => {
        const tags = deviceStore.tags({ enabled: true, type: "default" }) || {};
        const typeaheadOptions: ITypeaheadOption[] = [];
        for (const key of Object.keys(withoutReservedTags(tags))) {
            tags[key].forEach(tag => {
                if (tag) {
                    typeaheadOptions.push({
                        key,
                        value: tag,
                    });
                }
                if (
                    !tag &&
                    !typeaheadOptions.find(option => option.key === key) &&
                    !typeaheadOptions.find(option => option.key === "*")
                ) {
                    typeaheadOptions.push({
                        key,
                        value: `*`,
                    });
                }
            });
        }
        setExistingTagsOptions(typeaheadOptions);
    }, [deviceStore]);

    const addOption = () => {
        if (!newLabel || !newValue) {
            return;
        }

        const options = param?.options || [];
        onUpdate({
            ...param,
            options: [
                ...options,
                {
                    id: crypto.randomUUID(),
                    label: newLabel,
                    value: newValue,
                    description: newDescription || "",
                    tags: newTags,
                },
            ],
        });
        setNewLabel("");
        setNewValue("");
        setNewDescription("");
        setNewTags({});
    };

    const updateOption = (
        optionId: string,
        updates: Partial<{
            label: string;
            value: string;
            description: string;
            tags: ITags;
        }>,
    ) => {
        const options = param.options || [];

        const updatedOptions = options.map(opt =>
            opt.id === optionId ? { ...opt, ...updates } : opt,
        );
        onUpdate({
            ...param,
            options: updatedOptions,
        });
    };

    const removeOption = (optionId: string) => {
        const options = param.options || [];
        onUpdate({
            ...param,
            options: options.filter(opt => opt.id !== optionId),
        });
    };

    const handleNewTagsChange = (options: ITypeaheadOption[]) => {
        const tags: ITags = {};
        options.forEach(option => {
            tags[option.key] = option.value;
        });
        setNewTags(tags);
    };

    const handleExistingTagsChange = (
        optionId: string,
        options: ITypeaheadOption[],
    ) => {
        const tags: ITags = {};
        options.forEach(option => {
            tags[option.key] = option.value;
        });
        updateOption(optionId, { tags });
    };

    return (
        <div className="space-y-4">
            <FormItem>
                <FormLabel>Default Value</FormLabel>
                <SearchableDropdown
                    placeholder={`Select ${param.name.toLowerCase()}`}
                    options={[
                        {
                            value: "",
                            label: "Choose option",
                        },
                        ...(param.options ?? []),
                    ].map(opt => ({
                        value: opt.value,
                        label: opt.label,
                    }))}
                    selectedValue={String(param.value)}
                    onChange={value => onUpdate({ value: value.value })}
                />
            </FormItem>

            <div className="space-y-2">
                <p className="shrink-0 text-sm font-medium">Options</p>
                <div className="flex items-center gap-2">
                    <FormItem className="flex-1">
                        <FormControl>
                            <Input
                                value={newLabel}
                                onChange={e => setNewLabel(e.target.value)}
                                placeholder="New option label"
                            />
                        </FormControl>
                    </FormItem>
                    <FormItem className="flex-1">
                        <FormControl>
                            <Input
                                value={newValue}
                                onChange={e => setNewValue(e.target.value)}
                                placeholder="New option value"
                            />
                        </FormControl>
                    </FormItem>
                    <FormItem className="flex-1">
                        <FormControl>
                            <Input
                                value={newDescription}
                                onChange={e =>
                                    setNewDescription(e.target.value)
                                }
                                placeholder="New option description"
                            />
                        </FormControl>
                    </FormItem>
                    <FormItem className="flex-1">
                        <FormControl>
                            <TypeaheadInput
                                placeholder="Add tags"
                                initialOptions={convertTagsToTypeaheadOptions(
                                    newTags,
                                )}
                                onOptionsChange={handleNewTagsChange}
                                availableOptions={existingTagsOptions}
                            />
                        </FormControl>
                    </FormItem>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={addOption}
                        disabled={!newLabel || !newValue}
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                </div>

                <div className="space-y-2">
                    {param?.options?.map(opt => (
                        <div key={opt.id} className="flex items-center gap-2">
                            <FormItem className="flex-1">
                                <FormControl>
                                    <Input
                                        value={opt.label}
                                        onChange={e =>
                                            updateOption(opt.id, {
                                                label: e.target.value,
                                            })
                                        }
                                        placeholder="Option label"
                                    />
                                </FormControl>
                            </FormItem>

                            <FormItem className="flex-1">
                                <FormControl>
                                    <Input
                                        value={opt.value}
                                        onChange={e =>
                                            updateOption(opt.id, {
                                                value: e.target.value,
                                            })
                                        }
                                        placeholder="Option value"
                                    />
                                </FormControl>
                            </FormItem>

                            <FormItem className="flex-1">
                                <FormControl>
                                    <Input
                                        value={opt.description}
                                        onChange={e =>
                                            updateOption(opt.id, {
                                                description: e.target.value,
                                            })
                                        }
                                        placeholder="Option description"
                                    />
                                </FormControl>
                            </FormItem>

                            <FormItem className="flex-1">
                                <FormControl>
                                    <TypeaheadInput
                                        placeholder="Add tags"
                                        initialOptions={convertTagsToTypeaheadOptions(
                                            opt.tags || {},
                                        )}
                                        onOptionsChange={options =>
                                            handleExistingTagsChange(
                                                opt.id,
                                                options,
                                            )
                                        }
                                        availableOptions={existingTagsOptions}
                                    />
                                </FormControl>
                            </FormItem>

                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeOption(opt.id)}
                            >
                                <Trash className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
